import React from 'react';

import DocumentPages from 'components/shared/DocumentSheet/DocumentPages';
import DocFooter from 'components/shared/DocumentSheet/components/DocFooter';
import DocHeader from 'components/shared/DocumentSheet/components/DocHeader';
import DocCompactHeader from 'components/shared/DocumentSheet/components/DocCompactHeader';

import { useInvoiceDocumentModel } from './lib/useInvoiceDocumentModel';

/**
 * @param {object} params
 * @param {object} [params.practice] - Overrides the practice in state; the
 *   public invoice page has no practice session and passes the one the token
 *   resolved to.
 * @param {object} [params.modules] - Same, for the module flags.
 * @param {boolean} [params.withAging] - False where the practice-authed aging
 *   endpoint is not reachable (the public page).
 * @param {boolean} [params.fluid] - One growing sheet instead of paper pages.
 * @param {boolean} [params.fullWidth] - With `fluid`, fill the container.
 * @param {boolean} [params.fillHeight] - With `fluid`, keep the footer on the
 *   container's bottom edge.
 * @param {number} [params.zoom] - With `fluid`, scale the document's type — the
 *   token page reads at the app's body size rather than the print scale.
 * @param {React.ReactNode} [params.footerNotes] - Extra fine print under the
 *   footer line; the token page puts its GFE and billing-help notices there.
 */
const InvoiceDocument = ({
  invoice,
  externalInvoice,
  practice: practiceProp,
  modules: modulesProp,
  surcharge: surchargeProp,
  withAging = true,
  fluid = false,
  fullWidth = false,
  fillHeight = false,
  zoom,
  footerNotes,
  billedToAction
}) => {
  const { practice, blocks, landscape, isGfe, invoiceNumber, compactMeta, docLines, legal } =
    useInvoiceDocumentModel({
      invoice,
      externalInvoice,
      practice: practiceProp,
      modules: modulesProp,
      surcharge: surchargeProp,
      withAging,
      billedToAction
    });

  return (
    <DocumentPages
      blocks={blocks}
      landscape={landscape}
      fluid={fluid}
      fullWidth={fullWidth}
      fillHeight={fillHeight}
      zoom={zoom}
      data-invoice-id={invoice?.id}
      renderHeader={(pageIndex) =>
        pageIndex === 0 ? (
          <DocHeader
            practice={practice}
            title={isGfe ? 'Good Faith Estimate' : 'Invoice'}
            lines={docLines}
          />
        ) : (
          <DocCompactHeader
            practice={practice}
            reference={invoiceNumber ? `Invoice ${invoiceNumber}` : ''}
            meta={compactMeta}
          />
        )
      }
      renderFooter={({ page, pageCount }) => (
        <DocFooter
          legal={legal}
          notes={footerNotes}
          page={fluid ? '' : `Page ${page} of ${pageCount}`}
        />
      )}
    />
  );
};

export default InvoiceDocument;
